import React, { useEffect, useRef, useState } from 'react';
import {
    listProductImportBatches,
    ProductImportBatch,
    ProductImportBatchStatus,
} from '../../services/productImport.service';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import { useResponsive } from '../../hook/useResponsive';
import ViewIcon from '../../images/ViewIcon';
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
    formatUpdatedAt,
    t,
} from '../../components/list';
import {
    IconArrowLeft,
    IconUploadCloud,
    STATUS_PALETTE,
    TOKENS,
    ghostButton,
    primaryButton,
} from './productImportShared';

const BATCH_STATUS: Record<ProductImportBatchStatus, { label: string; bg: string; fg: string; dot: string }> = {
    PREVIEWED: { label: 'Previewed', bg: STATUS_PALETTE.pendingBg, fg: STATUS_PALETTE.pendingText, dot: STATUS_PALETTE.pendingDot },
    CONFIRMING: { label: 'Confirming', bg: STATUS_PALETTE.pendingBg, fg: STATUS_PALETTE.pendingText, dot: STATUS_PALETTE.pendingDot },
    CONFIRMED: { label: 'Confirmed', bg: STATUS_PALETTE.readyBg, fg: STATUS_PALETTE.readyText, dot: STATUS_PALETTE.readyDot },
    FAILED: { label: 'Failed', bg: STATUS_PALETTE.needsBg, fg: STATUS_PALETTE.needsText, dot: STATUS_PALETTE.needsDot },
    EXPIRED: { label: 'Expired', bg: STATUS_PALETTE.skipBg, fg: STATUS_PALETTE.skipText, dot: STATUS_PALETTE.skipDot },
};

const batchStatusLabel = (status: ProductImportBatchStatus, language: any) => {
    const label = BATCH_STATUS[status]?.label ?? String(status ?? '');
    return t(language, label, label);
};

/** The shared StatusChip look, with a translated label. */
const BatchStatusPill: React.FC<{ status: ProductImportBatchStatus; language: any }> = ({ status, language }) => {
    const s = BATCH_STATUS[status] ?? BATCH_STATUS.EXPIRED;
    return (
        <span
            className="font-inter"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: '100%',
                background: s.bg,
                color: s.fg,
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: '18px',
                whiteSpace: 'nowrap',
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flex: 'none' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{batchStatusLabel(status, language)}</span>
        </span>
    );
};

/** A count cell: green / red when the tone applies and the value is non-zero, dim at zero. */
const countCell = (get: (b: ProductImportBatch) => number, tone?: 'ready' | 'needs') => (b: ProductImportBatch) => {
    const value = Number(get(b) ?? 0);
    let color = TOKENS.text;
    if (value === 0) color = TOKENS.textDim;
    else if (tone === 'ready') color = STATUS_PALETTE.readyText;
    else if (tone === 'needs') color = STATUS_PALETTE.needsText;
    return (
        <span className="font-inter" style={{ fontSize: 14, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
            {value}
        </span>
    );
};

const ProductImportHistory: React.FC = () => {
    const preLanguage = localStorage.getItem('language') || 'en';
    const { isMobile } = useResponsive();
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(window.languageContext?.language ?? null);

    useEffect(() => {
        if (window.languageContext) setLanguage(window.languageContext.language);
    }, [window.languageContext]);

    const tr = (key: string) => t(language, key, key);
    const openBatch = (b: ProductImportBatch) => webNavigate(`/${preLanguage}/items/import/batches/${b.id}`);

    /** The batches endpoint takes page / limit only (no search, filter or sort). */
    const fetchBatches = async (query: ListQuery) => {
        webLoadingFalse();
        const res = await listProductImportBatches(query.page, query.limit);
        return { rows: res?.data ?? [], count: res?.total ?? 0 };
    };

    const baseColumns: ListColumn<ProductImportBatch>[] = [
        {
            key: 'filename',
            label: 'File',
            width: 300,
            minWidth: 160,
            hideable: false,
            value: (b, ctx) => b.filename ?? t(ctx.language, 'Untitled upload', 'Untitled upload'),
            render: cells.primary((b, ctx) => b.filename ?? t(ctx.language, 'Untitled upload', 'Untitled upload')),
            mobile: 'title',
        },
        {
            key: 'createdAt',
            label: 'Created',
            width: 140,
            minWidth: 100,
            value: (b, ctx) => formatUpdatedAt(b.createdAt, ctx.language),
            render: cells.date(b => b.createdAt),
            mobile: 'subtitle',
        },
        {
            key: 'totalRows',
            label: 'Total',
            width: 80,
            minWidth: 64,
            align: 'right',
            // Mobile subtitle reads "Today 10:14 · 120 rows".
            value: (b, ctx) => `${b.totalRows ?? 0} ${t(ctx.language, 'rows', 'rows')}`,
            render: countCell(b => b.totalRows),
            mobile: 'subtitle',
        },
        {
            key: 'readyCount',
            label: 'Ready',
            width: 80,
            minWidth: 64,
            align: 'right',
            render: countCell(b => b.readyCount, 'ready'),
            mobile: 'sheet',
        },
        {
            key: 'skipCount',
            label: 'Skip',
            width: 80,
            minWidth: 64,
            align: 'right',
            render: countCell(b => b.skipCount),
            mobile: 'sheet',
        },
        {
            key: 'needsAttentionCount',
            label: 'Needs attention',
            width: 130,
            minWidth: 96,
            align: 'right',
            render: countCell(b => b.needsAttentionCount, 'needs'),
            mobile: 'sheet',
        },
        {
            key: 'status',
            label: 'Status',
            width: 130,
            minWidth: 100,
            value: (b, ctx) => batchStatusLabel(b.status, ctx.language),
            render: (b, ctx) => <BatchStatusPill status={b.status} language={ctx.language} />,
            mobile: 'trailing',
        },
    ];
    // Every cell opens the batch, so the whole row stays clickable like the old cards.
    const columns = baseColumns.map(column => ({ ...column, onClick: openBatch }));

    const rowActions: ListRowAction<ProductImportBatch>[] = [
        {
            key: 'open',
            label: 'Open',
            icon: <ViewIcon />,
            sheetIcon: 'chev',
            onRun: openBatch,
        },
    ];

    const emptyState = {
        title: tr('No imports yet'),
        desc: tr('Upload your first XLSX to bring product data into the catalogue in bulk.'),
        img: <IconUploadCloud size={36} color={TOKENS.teal} />,
    };

    const list = (
        <ListScreen
            ref={listRef}
            language={language}
            columns={columns}
            fetchRows={fetchBatches}
            search={false}
            rowActions={rowActions}
            countNoun="imports"
            emptyState={emptyState}
        />
    );

    return (
        <div
            style={{
                padding: isMobile ? '16px 0 24px' : '32px 32px 24px',
                background: TOKENS.bg,
                // Own scroll context: the host shell clips overflow on its content area.
                height: '100%',
                overflowY: 'auto',
                boxSizing: 'border-box',
                display: isMobile ? undefined : 'flex',
                flexDirection: isMobile ? undefined : 'column',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                }}
            >
                <div style={{ minWidth: 0 }}>
                    <button
                        type="button"
                        onClick={() => webNavigate(`/${preLanguage}/items`)}
                        style={{
                            ...ghostButton(),
                            paddingLeft: 0,
                            marginBottom: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <IconArrowLeft size={14} color={TOKENS.teal} /> {tr('Back to Items')}
                    </button>
                    <h1
                        style={{
                            margin: 0,
                            color: TOKENS.tealDark,
                            fontSize: isMobile ? 24 : 32,
                            fontWeight: 700,
                            letterSpacing: -0.6,
                            lineHeight: 1.15,
                        }}
                    >
                        {tr('Import history')}
                    </h1>
                    <p style={{ margin: '8px 0 0', color: TOKENS.textMuted, fontSize: 14, maxWidth: 520 }}>
                        {tr("Every upload you've made — previewed, confirmed, failed, or expired.")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => webNavigate(`/${preLanguage}/items/import`)}
                    style={{ ...primaryButton(), background: TOKENS.tealGradient }}
                >
                    + {tr('New import')}
                </button>
            </div>

            {isMobile ? (
                list
            ) : (
                // Fills the remaining height; below 420px the page scrolls instead of squashing the table.
                <div style={{ flex: '1 0 420px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>{list}</div>
            )}
        </div>
    );
};

export default ProductImportHistory;
